import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, type ComponentType } from "react";
import { ArrowRight, HeartPulse, Activity, Brain, Home as HomeIcon, FileText, Star, MapPin, CheckCircle2, Clock, Stethoscope, ShoppingBag, Video } from "lucide-react";
import { LungsIcon } from "@/components/icons/LungsIcon";
import { PageShell } from "@/components/PageShell";
import { SITE } from "@/lib/site";
import { Deferred } from "@/components/Deferred";
import { HERO } from "@/lib/images";
import { Reveal, RevealGroup } from "@/components/Reveal";


// The shop showcase fetches live product data and is below the hero, so it is
// code-split and mounted after the browser goes idle. The hero image is
// preloaded automatically by React because it has fetchPriority="high".
const ProductShowcase = lazy(() =>
  import("@/components/shop/ProductShowcase").then((m) => ({ default: m.ProductShowcase }))
);

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Physiotherapist in Centurion | N S Physiotherapy" },
      { name: "description", content: "Trusted physiotherapist in Centurion for back pain, neck pain, sports injuries and home visits. Same day appointments, medical aid accepted. Book online." },
      { property: "og:title", content: "Physiotherapist in Centurion | N S Physiotherapy" },
      { property: "og:description", content: "In clinic and home visit physiotherapy in Centurion and Midrand. Book online or WhatsApp today." },
      { property: "og:url", content: "https://nsphysiotherapy.co.za/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://nsphysiotherapy.co.za/" }],
  }),
});

const services: Array<{ icon: ComponentType<{ className?: string }>; title: string; desc: string; to?: string }> = [
  { icon: HeartPulse, title: "Back & Neck Pain", desc: "Reduce tension and restore movement through assessment, manual therapy and targeted exercise.", to: "/services/back-pain-physiotherapy" },
  { icon: Activity, title: "Sports Injuries", desc: "Recover faster, return to sport stronger and prevent recurring injuries.", to: "/services/sports-injury-physiotherapy" },
  { icon: Brain, title: "Stroke & Neuro Rehab", desc: "Tailored programmes for stroke, Parkinson's and cerebral palsy.", to: "/services/stroke-rehabilitation" },
  { icon: Stethoscope, title: "Post Operative Rehab", desc: "Structured rehabilitation to rebuild strength and confidence after surgery.", to: "/services/post-operative-rehabilitation" },
  { icon: LungsIcon, title: "Chest Physiotherapy", desc: "Airway clearance, breathing exercises and respiratory recovery for adults and children.", to: "/services/chest-physiotherapy" },
  { icon: HomeIcon, title: "Home Physiotherapy", desc: "Professional treatment delivered in the comfort of your home or office.", to: "/physio-at-home" },
  { icon: Video, title: "Online Physiotherapy", desc: "Video consultations for assessment, exercise rehabilitation and follow up care, wherever you are.", to: "/services/online-physiotherapy" },
  { icon: FileText, title: "Medico Legal", desc: "Court ready, objective assessments and reports for insurance and legal matters.", to: "/medico-legal" },
];

const reasons = [
  "Qualified physiotherapist (BSc, UCT)",
  "In clinic and at home visits",
  "Same day appointments available",
  "Medical aid claims at scheme rates",
  "Trusted network of GPs and specialists",
  "Evidence based, personalised care",
];

function Index() {
  return (
    <PageShell>
      {/* Hero, clean white */}
      <section className="bg-background">
        <div className="container-page py-10 md:py-16 grid gap-10 md:grid-cols-2 items-center">

          <div>
            <span className="rise-in inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary/80">
              Centurion & Midrand
            </span>
            <h1 className="rise-in mt-6 max-w-3xl font-display text-3xl font-bold leading-[1.15] text-primary md:text-4xl lg:text-5xl" style={{ animationDelay: "90ms" }}>
              In Clinic & Mobile Physiotherapy Services in{" "}
              <span className="headline-accent">Centurion & Midrand</span>
            </h1>

            <p className="rise-in mt-5 text-sm text-foreground/70 md:text-base" style={{ animationDelay: "170ms" }}>
              N S Physiotherapy is a trusted physiotherapist in Centurion offering same day appointments for back pain, neck pain, sports injuries, post operative recovery and home visits across Centurion and Midrand. No need to call, book online or WhatsApp.
            </p>
            <div className="rise-in mt-8 flex flex-wrap gap-3" style={{ animationDelay: "250ms" }}>
              <Link to="/booking" className="btn-hero inline-flex h-12 items-center gap-2 rounded-full px-7 font-semibold">
                Book Online <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="rise-in mt-6 flex items-center gap-3 text-sm text-muted-foreground" style={{ animationDelay: "330ms" }}>
              <div className="flex gap-0.5 text-yellow-500">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
              <span><span className="font-semibold text-primary">5.0</span> · Google reviews</span>
            </div>
          </div>
          <div className="rise-in relative" style={{ animationDelay: "150ms" }}>
            <img

              src={HERO.src}
              srcSet={HERO.srcSet}
              sizes={HERO.sizes}
              width={HERO.width}
              height={HERO.height}
              fetchPriority="high"
              alt="Nhlamulo Khosa, physiotherapist at N S Physiotherapy in Centurion"
              className="block w-full rounded-3xl shadow-[var(--shadow-elegant)] ring-1 ring-border"
            />
          </div>
        </div>
      </section>


      {/* Services */}
      <section className="border-t border-border">
        <div className="container-page py-20">
          <Reveal className="mb-12 max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">What we do</div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">Our services</h2>
            <p className="mt-3 text-foreground/70">Comprehensive physiotherapy tailored to your goals, from everyday pain to complex rehabilitation.</p>
          </Reveal>
          <RevealGroup className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {services.map((s) => {
              const content = (
                <>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-primary">{s.title}</h3>
                  <p className="mt-2 text-sm text-foreground/70">{s.desc}</p>
                  {s.to && (
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:underline">
                      Learn more <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </>
              );
              const className = "group block rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[var(--shadow-soft)]";
              return s.to ? (
                <Link key={s.title} to={s.to} className={className}>{content}</Link>
              ) : (
                <article key={s.title} className={className}>{content}</article>
              );
            })}
          </RevealGroup>

        </div>
      </section>

      {/* Shop call-out */}
      <section className="border-t border-border">

        <div className="container-page py-10 md:py-12 grid gap-8 items-center md:grid-cols-[1fr_1fr]">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">

              <ShoppingBag className="h-3.5 w-3.5" /> Physio Shop
            </div>
            <h2 className="mt-3 font-display text-base md:text-lg font-semibold tracking-wide text-primary">Physiotherapy-Recommended Rehabilitation Equipment</h2>
            <Link to="/shop" className="mt-5 btn-hero inline-flex h-11 items-center gap-2 rounded-full px-5 font-semibold">
              Visit the shop <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Deferred>

            <Suspense fallback={null}>
              <ProductShowcase />
            </Suspense>
          </Deferred>
        </div>
      </section>

      {/* Assessment CTA */}
      <section className="border-t border-border py-12">
        <div className="container-page">
          <Reveal className="rounded-3xl border border-border bg-card p-8 md:p-12 shadow-[var(--shadow-soft)] grid gap-8 md:grid-cols-[1.4fr_1fr] items-center">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary">Back or neck pain?</h2>
              <p className="mt-3 max-w-xl text-foreground/80">Take our free online assessment to understand your pain and next steps.</p>
            </div>
            <div className="md:text-right">
              <Link to="/back-and-neck-assessment" className="btn-hero inline-flex h-12 items-center gap-2 rounded-full px-6 font-semibold">
                Start assessment <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>


      </section>

      {/* Why choose */}
      <section className="border-t border-border">
        <div className="container-page py-20 grid gap-10 md:grid-cols-2 items-start">
          <Reveal>
            <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Why choose us</div>

            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">Coordinated, professional care</h2>
            <p className="mt-4 text-foreground/70">Based at Eco Park Medical & Dental Centre in Centurion, we collaborate with a trusted network of healthcare professionals, from GPs and psychologists to dentists and optometrists, so your care is joined up under one roof.</p>
            <Link to="/our-healthcare-network" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              Meet our healthcare network <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <RevealGroup as="ul" className="grid gap-3 sm:grid-cols-2">

            {reasons.map((r) => (
              <li key={r} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                <span className="text-foreground/80">{r}</span>
              </li>
            ))}
          </RevealGroup>

        </div>
      </section>


      {/* Testimonials */}
      <section className="border-t border-border">
        <div className="container-page py-20">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Testimonials</div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">Trusted by our patients</h2>
          </Reveal>
          <RevealGroup className="mt-10 grid gap-5 md:grid-cols-3">

            {[
              { name: "Rivalani Peaceful", text: "The service is 100/10 and the place is always clean." },
              { name: "Xitshembhiso Promise", text: "I highly recommend Nhlamulo, the best physiotherapist." },
              { name: "Nsuku Baloyi", text: "Professional, attentive and truly caring." },
            ].map((t) => (
              <blockquote key={t.name} className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-soft)]">
                <div className="flex gap-0.5 text-yellow-500">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                <p className="mt-4 text-foreground/80">"{t.text}"</p>
                <footer className="mt-4 text-sm font-semibold text-primary"> {t.name}</footer>
              </blockquote>
            ))}
          </RevealGroup>

        </div>
      </section>

      {/* Contact / Map */}
      <section className="border-t border-border">
        <div className="container-page py-20 grid gap-8 md:grid-cols-2 items-stretch">
          <Reveal className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Visit us</div>
            <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-primary">Centurion Clinic</h2>
            <p className="mt-3 text-foreground/70">Come and see us in person, by appointment during business hours.</p>
            <ul className="mt-5 space-y-3 text-sm text-foreground/80">
              <li className="flex gap-3"><MapPin className="h-5 w-5 text-primary shrink-0" /> {SITE.addressCenturion}</li>
              <li className="flex gap-3"><Clock className="h-5 w-5 text-primary shrink-0" /> Open daily · 07:00 – 20:00</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/booking" className="btn-hero inline-flex h-11 items-center gap-2 rounded-full px-5 font-semibold">Book appointment</Link>
              <a href="https://maps.google.com/?q=270+Witch+Hazel+Avenue,+Eco+Park+Estate,+Centurion" target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-5 font-semibold text-primary hover:bg-accent">Get directions</a>
            </div>
          </Reveal>
          <div className="min-h-[320px] rounded-3xl overflow-hidden border border-border">

            <iframe title="Map to N S Physiotherapy Centurion" src="https://www.google.com/maps?q=270+Witch+Hazel+Avenue,+Eco+Park+Estate,+Centurion&output=embed" className="h-full w-full min-h-[320px]" loading="lazy" />
          </div>
        </div>
      </section>
    </PageShell>
  );
}
